import { useCallback, useMemo, useState } from 'react';

import type {
  PredefinedMessage,
  PredefinedMessageFiltersHookResult,
  PredefinedMessagesFiltersState,
} from '@/types/predefined-messages';

function toggleInSet(current: Set<string>, id: string): Set<string> {
  const next = new Set(current);

  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }

  return next;
}

export function usePredefinedMessageFilters(
  baseMessages: PredefinedMessage[]
): PredefinedMessageFiltersHookResult {
  const [query, setQuery] = useState('');
  const [availableToFilter, setAvailableToFilter] = useState<Set<string>>(() => new Set());
  const [recipientsFilter, setRecipientsFilter] = useState<Set<string>>(() => new Set());

  const toggleAvailableTo = useCallback((id: string) => {
    setAvailableToFilter((current) => toggleInSet(current, id));
  }, []);

  const clearAvailableToFilter = useCallback(() => {
    setAvailableToFilter(new Set());
  }, []);

  const selectAllAvailableTo = useCallback((ids: string[]) => {
    setAvailableToFilter(new Set(ids));
  }, []);

  const toggleRecipient = useCallback((id: string) => {
    setRecipientsFilter((current) => toggleInSet(current, id));
  }, []);

  const clearRecipientsFilter = useCallback(() => {
    setRecipientsFilter(new Set());
  }, []);

  const selectAllRecipients = useCallback((ids: string[]) => {
    setRecipientsFilter(new Set(ids));
  }, []);

  const filteredMessages = useMemo(() => {
    let list = baseMessages;
    const trimmed = query.trim().toLowerCase();

    if (trimmed) {
      list = list.filter((message) => message.message.toLowerCase().includes(trimmed));
    }

    if (availableToFilter.size > 0) {
      list = list.filter((message) =>
        message.availableToIds.some((id) => availableToFilter.has(id))
      );
    }

    if (recipientsFilter.size > 0) {
      list = list.filter((message) => message.recipientIds.some((id) => recipientsFilter.has(id)));
    }

    return list;
  }, [availableToFilter, baseMessages, query, recipientsFilter]);

  const filters = useMemo<PredefinedMessagesFiltersState>(
    () => ({
      query,
      onQueryChange: setQuery,
      availableToFilter,
      onToggleAvailableTo: toggleAvailableTo,
      onSelectAllAvailableTo: selectAllAvailableTo,
      onClearAvailableTo: clearAvailableToFilter,
      recipientsFilter,
      onToggleRecipient: toggleRecipient,
      onSelectAllRecipients: selectAllRecipients,
      onClearRecipients: clearRecipientsFilter,
    }),
    [
      query,
      availableToFilter,
      toggleAvailableTo,
      selectAllAvailableTo,
      clearAvailableToFilter,
      recipientsFilter,
      toggleRecipient,
      selectAllRecipients,
      clearRecipientsFilter,
    ]
  );

  return {
    filters,
    filteredMessages,
  };
}
